import React, { useEffect, useState } from "react";
import { WrapperSeamless } from "../../layout-components";
import Table from "../../components/tables/table";
import { Button } from "@material-ui/core";
import { useGetList, usePutData } from "../../hooks";
import { Select } from "../../components";
import { Link } from "react-router-dom";
import * as Api from "../../service";
import useStore from "../../context";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FileCopy from "@material-ui/icons/FileCopy";
import { useTranslation } from "localization/translation";
import {
  ADD_ONE_TO_ORDER,
  PLACEMENT_FILTER_DROPDOWN,
  SOMETHING_WENT_WRONG,
  STATUS_DROPDOWN,
} from "Constants";
import { dateFormatter } from "helpers/dateFormatter";

import dragIcon from "assets/images/dragIcon.svg";

const baseImageUrl = process.env.REACT_APP_imagesBaseUrl;

export default function AdvertisementsList() {
  const [activateDrag, setActivateDrag] = useState(false);
  const [dragElement, setDragElement] = useState(null);
  const [copyingId, setCopyingId] = useState(null);

  const { setRemove, language, query, setQuery, setSuccessMsg, setErrorMsg } =
    useStore();
  const translation = useTranslation();

  const limit = 20;
  const isPageCorrect = true;

  const [items, total, refresh, setItems] = useGetList(
    Api.advertisement.getAll,
    query,
    limit,
    isPageCorrect
  );

  const orderCallback = usePutData(Api.goodsTypeOrder.update, refresh, true);

  const dragEnd = () => {
    setActivateDrag(false);
    setDragElement(null);
    const newOrderItems = items.map((value, index) => {
      return { id: value.id, order: index + ADD_ONE_TO_ORDER };
    });
    orderCallback({ advertisementDetails: newOrderItems });
  };

  const handleStatusChange = (val) => {
    setQuery((st) => ({ ...st, status: val }));
  };

  const handlePlacementChange = (val) => {
    setQuery((st) => ({ ...st, placement: val }));
  };

  const handleDelete = (id) => {
    setRemove(() => () => Api.advertisement.delete(id).then(refresh));
  };

  const handleCopy = async (id) => {
    if (copyingId) return;

    setCopyingId(id);
    try {
      await Api.main.copyAdvertisement(id);
      setSuccessMsg(translation.copied);
      refresh();
    } catch (e) {
      setErrorMsg(e?.message || SOMETHING_WENT_WRONG);
    } finally {
      setCopyingId(null);
    }
  };

  useEffect(() => {
    if (language && `name_${language}` !== query.sort) {
      setQuery((prev) => ({
        ...prev,
        sort: `name_${language}`,
      }));
    }
  }, [language, query.sort, setQuery]);

  const rows = items?.map((item) => (
    <>
      <td className={`text-left px-4 ${activateDrag && "background_unset"}`}>
        <div className="font-size-sm">
          <span className="drag-icon-field">
            <span
              className="drag-icon-background"
              onMouseDown={() => {
                setActivateDrag(true);
                setDragElement(item.id);
              }}
              onMouseUp={() => {
                setActivateDrag(false);
                setDragElement(null);
              }}
            ></span>
            <img
              src={dragIcon}
              alt="Drag"
              className="drag-icon"
              id="dragIcon"
            />
          </span>
          <img
            className="suppport-icon"
            src={`${baseImageUrl}${
              item?.[`image_${language}`]
                ? item?.[`image_${language}`]
                : item?.image_en
            }`}
            alt="Icon"
          />
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{item?.[`title_${language}`]}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {item.start_time ? dateFormatter(item.start_time, "DD/MM/YYYY") : ""}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {item.end_time ? dateFormatter(item.end_time, "DD/MM/YYYY") : ""}
        </div>
      </td>
      <td
        className={`text-right actions_filed ${
          activateDrag && "background_unset"
        }`}
      >
        <Button
          onClick={() => handleCopy(item.id)}
          disabled={copyingId === item.id}
          title={translation.copy}
          className="btn-neutral-first mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
        >
          <FileCopy color="primary" />
        </Button>

        <Link to={`/advertisements/create/${item.id}`}>
          <Button className="btn-neutral-first mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
            <EditIcon color="primary" />
          </Button>
        </Link>

        <Button
          onClick={() => handleDelete(item.id)}
          className="btn-neutral-danger mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
        >
          <DeleteIcon color="error" />
        </Button>
      </td>
    </>
  ));

  return (
    <WrapperSeamless>
      <Table
        title={translation.advertisements}
        items={items}
        setItems={setItems}
        total={total}
        limit={limit}
        fields={
          <>
            <th className="text-left px-4">
              <span className="mr-3">{translation.image}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.title}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.start_date}</span>
            </th>
            <th className="text-left px-4">
              <span className="mr-3">{translation.end_date}</span>
            </th>
          </>
        }
        header={
          <>
            <Select
              onChange={(val) => handleStatusChange(val)}
              value={query?.status ?? []}
              field={"name"}
              label={translation.status}
              noCard
              options={STATUS_DROPDOWN}
              className="ml-4 mr-auto w-25"
              isFieldTranslate
            />
            <Select
              onChange={(val) => handlePlacementChange(val)}
              value={query?.placement ?? []}
              field={"name"}
              label={translation.placement}
              noCard
              options={PLACEMENT_FILTER_DROPDOWN}
              className="ml-4 mr-auto w-25"
              multiple
              isFieldTranslate
            />
          </>
        }
        rows={rows}
        createLink="advertisements/create"
        activateDrag={activateDrag}
        orderCallback={orderCallback}
        dragElement={dragElement}
        dragEnd={dragEnd}
      />
    </WrapperSeamless>
  );
}
